import { AgGridTable } from '@components/AgGrid/AgGridTable';
import { HistoryBadgeFilter } from '@components/HistoryBadgeFilter';
import { HistoryDateRangeFilter } from '@components/HistoryDateRangeFilter';
import { ModalDialog } from '@components/ModalDialog';
import type { HistoryChangeType } from '@gql/graphql';
import { usePageFilterSaver } from '@hooks/usePageFilterSaver';
import { cnMixSpace, Text } from '@uikit';

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { historyStylesRules } from './helpers';
import { catalogRatiosHistoryRequestId, useHistoryColumns, useHistoryData } from './hooks';

import Style from './catalogRatiosHistory.module.css';

type TCatalogRatiosHistoryProps = {
    closeHistory: () => void;
    catalogId: string | undefined;
};

export const CatalogRatiosHistory = ({ closeHistory, catalogId }: TCatalogRatiosHistoryProps) => {
    const { dataSource, tableRef, handlerFilterChange, countsObj, tableRdy, dateFilterValues } =
        useHistoryData(catalogId);

    const columns = useHistoryColumns({
        catalogId,
        handlerFilterChange,
        tableRdy,
        dateFilterValues,
    });

    const { pathname } = useLocation();
    const { handleCleanEntityMemoizedValues } = usePageFilterSaver({ customSaveKey: catalogRatiosHistoryRequestId });
    const memoRatiosHistoryKey = pathname.replace('/replace', '');

    const onCloseHistory = useCallback(() => {
        closeHistory();
        handleCleanEntityMemoizedValues({
            pathNameKey: memoRatiosHistoryKey,
        });
    }, [closeHistory, handleCleanEntityMemoizedValues, memoRatiosHistoryKey]);

    return (
        <ModalDialog
            isOpen
            title={
                <div className={Style.titleBlock}>
                    <div className={Style.titleLeft}>
                        <Text
                            view="secondary"
                            lineHeight="l"
                            size="s"
                            weight="semibold"
                            transform="uppercase"
                            className={cnMixSpace({
                                mR: 's',
                            })}
                        >
                            Просмотр истории изменений
                        </Text>
                        <HistoryDateRangeFilter onDateRangeChange={handlerFilterChange} />
                    </div>
                    <HistoryBadgeFilter<HistoryChangeType>
                        handlerBadgeFilterChange={handlerFilterChange}
                        countsObj={countsObj}
                    />
                </div>
            }
            onClose={onCloseHistory}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div className={Style.modalBody}>
                {!tableRdy && (
                    <Text view="secondary" size="xs" className={Style.tableOverlay}>
                        Для отображения данных выберите период
                    </Text>
                )}

                <AgGridTable
                    key="catalog-ratios-history"
                    ref={tableRef}
                    className={Style.historyTable}
                    datasource={dataSource}
                    rowModelType="infinite"
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    columnDefs={columns}
                    headerHeight={40}
                    rowClassRules={historyStylesRules}
                    suppressNoRowsOverlay
                    context={{ tableUniqueId: catalogRatiosHistoryRequestId }}
                />
            </div>
        </ModalDialog>
    );
};

CatalogRatiosHistory.displayName = 'CatalogRatiosHistory';
